import type {
  CausaleAssenza,
  DataISO,
  Dipendente,
  Evento,
  GiornoCalendario,
  ProgrammaSettimanale,
  Ruolo,
  TipoEvento,
} from './tipi.ts';
import type { EsitoVerifica, RichiestaDaVerificare } from './verifica.ts';

/**
 * Chiamate all'API.
 *
 * Un solo punto in cui si parla con il server: le intestazioni, il cookie di
 * sessione e la traduzione degli errori stanno qui, non sparsi nei componenti.
 */

export class ErroreRisposta extends Error {
  constructor(
    readonly stato: number,
    readonly codice: string,
    messaggio: string,
    readonly dettagli?: Record<string, unknown>,
  ) {
    super(messaggio);
    this.name = 'ErroreRisposta';
  }
}

async function chiama<T>(percorso: string, opzioni: RequestInit = {}): Promise<T> {
  const scrittura = opzioni.method !== undefined && opzioni.method !== 'GET';
  const risposta = await fetch(percorso, {
    ...opzioni,
    // Il cookie di sessione e' httpOnly: viaggia solo se lo si chiede.
    credentials: 'same-origin',
    headers: {
      ...(opzioni.body ? { 'Content-Type': 'application/json' } : {}),
      // Un modulo cross-site non puo' impostare questa intestazione senza un
      // preflight: e' la protezione CSRF, senza token da sincronizzare.
      ...(scrittura ? { 'X-Richiesta-Applicativa': '1' } : {}),
      ...opzioni.headers,
    },
  });

  if (!risposta.ok) {
    const corpo = (await risposta.json().catch(() => null)) as {
      errore?: string;
      messaggio?: string;
      dettagli?: Record<string, unknown>;
    } | null;
    throw new ErroreRisposta(
      risposta.status,
      corpo?.errore ?? 'ERRORE',
      corpo?.messaggio ?? `Richiesta fallita (${risposta.status})`,
      corpo?.dettagli,
    );
  }

  if (risposta.status === 204) return undefined as T;
  return (await risposta.json()) as T;
}

export interface Profilo {
  utenteId: string;
  dipendenteId: string;
  username: string;
  nomeCompleto: string;
  ruolo: Ruolo;
  sedeCodice: string;
  repartoCodice: string;
  puoForzareApprovazioni: boolean;
  puoAccedereReportistica: boolean;
  puoAccedereAnagrafica: boolean;
  /** La pagina dei programmi settimanali: la vedono anche i funzionari. */
  puoAccedereFunzionari: boolean;
  /** Puo' approvare in anticipo o rifiutare le richieste altrui. */
  puoDecidereRichieste: boolean;
  /** Il riposo produzione lo registra solo chi ha una delega. */
  puoInserireRiposoProduzione: boolean;
  /**
   * Gli anni apribili: quello corrente e, da luglio, il successivo.
   *
   * Li decide il server. Rifare il conto qui darebbe un altro risultato su un
   * portatile con la data sbagliata, e la richiesta verrebbe respinta senza
   * che si capisca perche'.
   */
  anniDisponibili: number[];
  /** Ultimo giorno inseribile: il tetto dei campi data. */
  ultimoGiornoInseribile: DataISO;
  /**
   * Primo giorno inseribile: il pavimento.
   *
   * Oggi per quasi tutti. Per chi scavalca i vincoli - amministratori e
   * responsabili - il primo gennaio dell'anno in corso, perche' correggere
   * quello che e' gia' successo fa parte del mestiere.
   */
  primoGiornoInseribile: DataISO;
  sediDelegate: string[];
  repartiDelegati: string[];
  deveCambiarePassword?: boolean;
}

export interface DatiCalendario {
  anno: number;
  dipendenti: Dipendente[];
  eventi: Evento[];
  programmi: ProgrammaSettimanale[];
  /** Ids su cui l'utente puo' scrivere: gia' risolti dal server. */
  dipendentiModificabili: string[];
}

export interface Delega {
  id: string;
  sedeCodice: string;
  sedeNome: string;
  /** Nullo quando la delega vale sull'intera sede. */
  repartoCodice: string | null;
  repartoNome: string | null;
}

export interface VoceAnagrafica {
  id: string;
  nomeCompleto: string;
  email: string | null;
  matricola: string | null;
  codiceFiscale: string | null;
  /** Ore di una giornata intera. Nullo = tempo pieno. */
  orarioSettimanale: number[];
  /** Utenza di servizio, non una persona: fuori da calendario, analisi, assenti. */
  tecnico: boolean;
  ruolo: Ruolo;
  stato: 'ATTIVO' | 'CESSATO';
  dataAssunzione: DataISO | null;
  dataCessazione: DataISO | null;
  repartoId: string;
  repartoCodice: string;
  repartoNome: string;
  sedeId: string;
  sedeCodice: string;
  sedeNome: string;
  /** Nullo per chi e' in anagrafica senza un accesso. */
  utenteId: string | null;
  username: string | null;
  accessoAttivo: boolean | null;
  deveCambiarePassword: boolean | null;
  ultimoAccesso: string | null;
  deleghe: Delega[];
}

export interface RepartoDisponibile {
  id: string;
  codice: string;
  nome: string;
  sedeId: string;
  sedeCodice: string;
  sedeNome: string;
}

export interface NuovaPersona {
  nomeCompleto: string;
  repartoId: string;
  ruolo: Ruolo;
  dataAssunzione: DataISO;
  email?: string | null;
  matricola?: string | null;
  codiceFiscale?: string | null;
  /** Ore lavorate per giorno, da lunedi' a domenica. Zero = non lavora. */
  orarioSettimanale: number[];
  /** Utenza di servizio. Si dichiara alla creazione e non si cambia dopo. */
  tecnico?: boolean;
  username?: string | null;
}

/** Restituita una volta sola alla creazione o all'azzeramento. */
export interface CredenzialiProvvisorie {
  username: string;
  passwordProvvisoria: string;
}

export interface NuovaRichiesta {
  dipendenteId: string;
  tipo: TipoEvento;
  dataInizio: DataISO;
  dataFine: DataISO;
  ore?: number | null;
  causale?: CausaleAssenza | null;
  nota?: string | null;
  urgente?: boolean;
}

export const api = {
  accedi: (username: string, password: string) =>
    chiama<Profilo>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  esci: () => chiama<{ uscito: boolean }>('/api/auth/logout', { method: 'POST' }),

  profilo: () => chiama<Profilo>('/api/auth/me'),

  cambiaPassword: (nuova: string) =>
    chiama<{ cambiata: boolean }>('/api/auth/cambia-password', {
      method: 'POST',
      body: JSON.stringify({ nuova }),
    }),

  richiediRecupero: (identificativo: string) =>
    chiama<{ ricevuta: boolean }>('/api/auth/recupero/richiedi', {
      method: 'POST',
      body: JSON.stringify({ identificativo }),
    }),

  confermaRecupero: (token: string, nuova: string) =>
    chiama<{ cambiata: boolean }>('/api/auth/recupero/conferma', {
      method: 'POST',
      body: JSON.stringify({ token, nuova }),
    }),

  giorni: (anno: number) => chiama<GiornoCalendario[]>(`/api/calendario/giorni?anno=${anno}`),

  calendario: (anno: number) => chiama<DatiCalendario>(`/api/calendario/dati?anno=${anno}`),

  mieiEventi: () =>
    chiama<(Evento & { approvazionePrevista: DataISO | null })[]>('/api/miei-eventi'),

  verifica: (richiesta: RichiestaDaVerificare) =>
    chiama<EsitoVerifica>('/api/eventi/verifica', {
      method: 'POST',
      body: JSON.stringify(richiesta),
    }),

  creaEvento: (richiesta: NuovaRichiesta) =>
    chiama<{ id: string; stato: string; avvisi: { codice: string; messaggio: string }[] }>(
      '/api/eventi',
      { method: 'POST', body: JSON.stringify(richiesta) },
    ),

  annullaEvento: (id: string, versione: number) =>
    chiama<{ annullato: boolean }>(`/api/eventi/${id}`, {
      method: 'DELETE',
      // Lock ottimistico: se qualcun altro ha toccato l'evento nel frattempo
      // il server risponde 409 invece di far vincere l'ultimo che ha premuto.
      headers: { 'If-Match': String(versione) },
    }),

  approvaEvento: (id: string) =>
    chiama<{ approvato: boolean }>(`/api/eventi/${id}/approva`, { method: 'POST' }),

  /** Il motivo e' obbligatorio: viene mostrato al dipendente sulla sua home. */
  rifiutaEvento: (id: string, motivo: string) =>
    chiama<{ rifiutato: boolean }>(`/api/eventi/${id}/rifiuta`, {
      method: 'POST',
      body: JSON.stringify({ motivo }),
    }),

  // --- Anagrafica: solo per amministratori ---

  anagrafica: () => chiama<VoceAnagrafica[]>('/api/anagrafica'),

  repartiDisponibili: () => chiama<RepartoDisponibile[]>('/api/anagrafica/reparti'),

  creaPersona: (persona: NuovaPersona) =>
    chiama<CredenzialiProvvisorie & { dipendenteId: string; utenteId: string }>(
      '/api/anagrafica',
      { method: 'POST', body: JSON.stringify(persona) },
    ),

  modificaPersona: (
    id: string,
    modifiche: Partial<
      Pick<
        NuovaPersona,
        | 'repartoId'
        | 'ruolo'
        | 'email'
        | 'matricola'
        | 'codiceFiscale'
        | 'orarioSettimanale'
        | 'dataAssunzione'
      >
    >,
  ) =>
    chiama<{ aggiornato: boolean }>(`/api/anagrafica/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(modifiche),
    }),

  cessaPersona: (id: string, dataCessazione: DataISO, motivo?: string) =>
    chiama<{ cessato: boolean }>(`/api/anagrafica/${id}/cessa`, {
      method: 'POST',
      body: JSON.stringify({ dataCessazione, motivo: motivo ?? null }),
    }),

  riattivaPersona: (id: string, dal: DataISO) =>
    chiama<{ riattivato: boolean }>(`/api/anagrafica/${id}/riattiva`, {
      method: 'POST',
      body: JSON.stringify({ dal }),
    }),

  azzeraPassword: (id: string) =>
    chiama<CredenzialiProvvisorie>(`/api/anagrafica/${id}/azzera-password`, { method: 'POST' }),

  concediDelega: (id: string, sedeId: string, repartoId: string | null) =>
    chiama<{ concessa: boolean }>(`/api/anagrafica/${id}/deleghe`, {
      method: 'POST',
      body: JSON.stringify({ sedeId, repartoId }),
    }),

  revocaDelega: (delegaId: string) =>
    chiama<{ revocata: boolean }>(`/api/anagrafica/deleghe/${delegaId}`, { method: 'DELETE' }),
};
